import Axios from 'axios';

import { INSTALLER_CONFIG } from 'utils/constants';

import { proxy } from '../shared';

const { REACT_APP_INSTALLER_URL, REACT_APP_INSTALLER_PORT } = process.env;

const axios = Axios.create({ timeout: 10 * 1000 });

export const baseURL = () => {
  const { port } = JSON.parse(
    localStorage.getItem(INSTALLER_CONFIG) ||
      '{"sslEnable":true,"port":"7777"}',
  );
  return `https://localhost.erply.com:${port}/api/v1`;
};
const parseResponse = (promise) => {
  let success = false;
  return promise
    .then(({ data }) => {
      success = true;
      //  TODO: fix once response form server is adjusted
      //  currently some errors have httpCode and Status differing
      //  once ths is fixed adjust this handler accordingly
      if (data.httpCode !== 200 || data.status !== 'OK') {
        throw Error(data.message);
      }
      return data;
    })
    .catch((err) => {
      if (success) {
        throw err;
      } else {
        console.error(err);
        throw new Error(
          'Server is not responding. Make sure it is installed and running properly',
        );
      }
    });
};

/**
 * Updates to latest version of Installer App
 * @returns {*}
 */
export const updateInstaller = () =>
  parseResponse(axios.get(`${baseURL()}/update`, { timeout: 60 * 1000 }));

/**
 * Return Installer App information
 */
export const getInfo = () => parseResponse(axios.get(`${baseURL()}/info`));

/**
 * Returns list of all available integrations in the cloud
 */
export const getAvailableIntegrations = () =>
  parseResponse(axios.get(`${baseURL()}/list`));

/**
 * Returns all available version for specified integration
 *
 * @param {String} entity - integration name
 */
export const getIntegrationVersion = (entity) =>
  parseResponse(axios.post(`${baseURL()}/list`, { entity }));

/**
 * Downloads the specified integration version
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const downloadIntegration = (entity, version) =>
  parseResponse(
    axios.post(
      `${baseURL()}/download`,
      { entity, version },
      { timeout: 60 * 1000 },
    ),
  );

/**
 * Updates the specified integration to the specified version
 * The MS version is updated but the configuration is preserved
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const updateIntegrationVersion = (entity, version) =>
  parseResponse(
    axios.post(
      `${baseURL()}/update`,
      { entity, version },
      { timeout: 60 * 1000 },
    ),
  );

/**
 * Installs integration version, only if it has already been downloaded
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const installIntegration = (entity, version) =>
  parseResponse(
    axios.post(
      `${baseURL()}/install`,
      { entity, version },
      { timeout: 60 * 1000 },
    ),
  );

/**
 * Removes specified integration version
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const removeIntegration = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/remove`, { entity, version }));

/**
 * Starts integration version, only if it has already been downloaded and installed
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const startIntegrationProcess = (entity, version) =>
  parseResponse(
    axios.post(
      `${baseURL()}/start`,
      { entity, version },
      { timeout: 60 * 1000 },
    ),
  );

/**
 * Starts integration version, only if it has already started
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const stopIntegrationProcess = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/stop`, { entity, version }));

/**
 * Starts integration version, only if it has already started
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const getIntegrationStatus = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/status`, { entity, version }));

/**
 * Starts integration version, only if it has already started
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const getIntegrationConfig = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/config/get`, { entity, version }));

/**
 * Starts integration version, only if it has already started
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 * @param {String} config - stringified JSON with the integration's configuration to be stored
 */
export const saveIntegrationConfig = (entity, version, config) =>
  parseResponse(
    axios.post(`${baseURL()}/config/save`, { entity, version, config }),
  );

/**
 * Fetches installer configuration
 */
export const getInstallerConfig = () =>
  getIntegrationConfig('./installer2', './');

/**
 * Updates installer configuration
 */
export const saveInstallerConfig = (config) =>
  saveIntegrationConfig('./installer2', './', config);

/**
 * Starts integration version, only if it has already started
 *
 * @param {String} entity - integration name
 * @param {String} version - semantic version of the integration exmpl. "1.0.0"
 */
export const getIntegrationReadme = (entity, version) =>
  parseResponse(axios.post(`${baseURL()}/config/save`, { entity, version }));

/**
 * Mock Installer response function for testing purposes
 *
 * @param {{message: string, data: any}} responseBody - response message and data
 * @param {String} errorMessage - error message to be returned
 *
 * @returns Returns `success response` if only first parameter is passed. Returns `error response` if error message is passed as second parameter
 */
export const mockInstallerResponse = (responseBody = {}, errorMessage = null) =>
  errorMessage
    ? {
        httpCode: 500,
        dateTime: '20191111150612',
        status: 'FAIL',
        message: errorMessage,
      }
    : {
        httpCode: 200,
        dateTime: '20191111150612',
        status: 'OK',
        message: responseBody.message,
        data: responseBody.data,
      };

export const testUrl = () =>
  `${proxy}${REACT_APP_INSTALLER_URL}:${REACT_APP_INSTALLER_PORT}/api/v1/`;
